import React, { lazy } from 'react';
import { useSelector } from 'react-redux';
import { MainContent } from '@bbkAdminComponents/app-layout/main-content';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { NotFoundPage } from '@bbkAdminComponents/not-found.page';
import UnifiedReportingDashboard from '@bbkAdminComponents/unified-plays/reporting/unified-reporting-dashboard';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { useCreatePagesListUrl, useUrl } from '@bbkAdminUtils/nav';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { useRoles } from '@bbkAdminUtils/roles';
import { usePendo } from '@bbkAdminUtils/usePendo';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import {
  CompatRoute,
  Redirect,
  Switch,
  useHistory,
} from '@bbkShared/react-router';

const Install = lazy(() => import('@bbkAdminComponents/settings/install'));
const Dashboard = lazy(() => import('@bbkAdminComponents/dashboard/dashboard'));
const Settings = lazy(() => import('@bbkAdminComponents/settings/settings'));
const Integrations = lazy(
  () => import('@bbkAdminComponents/settings/integrations')
);
const Setup = lazy(() => import('@bbkAdminComponents/settings/setup'));
const Developers = lazy(
  () => import('@bbkAdminComponents/settings/developers')
);
const Permissions = lazy(
  () => import('@bbkAdminComponents/settings/permissions')
);
const Users = lazy(() => import('@bbkAdminComponents/settings/users'));
const Plans = lazy(() => import('@bbkAdminComponents/settings/plans/plans'));

const PlayManager = lazy(
  () => import('@bbkAdminComponents/play/plays-manager')
);

const PlayEditor = lazy(() => import('@bbkAdminComponents/play/play-editor'));

const CreateEditPlay = lazy(
  () => import('@bbkAdminComponents/unified-plays/editor/create-edit-play')
);

const InviteUsers = lazy(
  () => import('@bbkAdminComponents/settings/invite-users')
);
const AttributionInsights = lazy(
  () => import('@bbkAdminComponents/insights/attribution-insights-report')
);
const TrendsReportGeneral = lazy(
  () => import('@bbkAdminComponents/trends-report/trends-report-general')
);
const CustomersPage = lazy(
  () => import('@bbkAdminComponents/customers/customers-page')
);
const CustomerDetailsPage = lazy(
  () => import('@bbkAdminComponents/customers/customer-details.page')
);
const DataManagement = lazy(
  () => import('@bbkAdminComponents/data-management/data-management')
);
const ConfirmationPages = lazy(
  () =>
    import('@bbkAdminComponents/experiences/funnels-management/cancel-confirmation-funnels/confirmation-pages')
);
const ConfirmationPageDesigner = lazy(
  () =>
    import('@bbkAdminComponents/experiences/funnels-management/cancel-confirmation-funnels/confirmation-page')
);
const PagesListPage = lazy(
  () =>
    import('@bbkAdminComponents/experiences/funnels-management/pages-list-page')
);
const PricingPageEditor = lazy(
  () =>
    import('@bbkAdminComponents/experiences/pricing-pages-editors/pricing-page-editor')
);
const PricingPagePreview = lazy(
  () =>
    import('@bbkAdminComponents/experiences/pricing-pages-editors/pricing-page-preview')
);
const PricingPageFullPreview = lazy(
  () =>
    import('@bbkAdminComponents/experiences/pricing-pages-editors/pricing-page-full-preview')
);
const AudiencesManagementPage = lazy(
  () => import('@bbkAdminComponents/experiences/audiences/audiences-page')
);
const PricingPagesList = lazy(
  () =>
    import('@bbkAdminComponents/experiences/pricing-pages-editors/pricing-pages-list')
);
const LossAversionListPage = lazy(
  () =>
    import('@bbkAdminComponents/experiences/loss-aversion/loss-aversions-list-page')
);
const LossAversionNewPage = lazy(
  () =>
    import('@bbkAdminComponents/experiences/loss-aversion/new-loss-aversion-page')
);
const OffersManagementPage = lazy(
  () => import('@bbkAdminComponents/experiences/offers/offers-management-page')
);
const OffersEditOfferPage = lazy(
  () => import('@bbkAdminComponents/experiences/offers/edit-offer-page')
);
const Designer = lazy(
  () => import('@bbkAdminComponents/designer/designer-page')
);
const ReasonsEditorPage = lazy(
  () => import('@bbkAdminComponents/experiences/funnel/reasons-editor-page')
);
const SubscriptionsPage = lazy(
  () => import('@bbkAdminComponents/subscriptions-page/subscriptions-page')
);
const AlertEditPage = lazy(
  () => import('@bbkAdminComponents/subscriptions-page/alerts/alert-edit-page')
);
const WebhookEditPage = lazy(
  () =>
    import('@bbkAdminComponents/subscriptions-page/webhooks/webhook-edit-page')
);
const ActivityLogPage = lazy(
  () => import('@bbkAdminComponents/activity-log/activity-log-page')
);
const OfferPerformance = lazy(
  () => import('@bbkAdminComponents/offer-performance/offer-performance-v2')
);
const ExperiencePerformanceReportV2 = lazy(
  () =>
    import('@bbkAdminComponents/experience-performance-report/exp-perf-report-v2')
);

const RetainedRevenueCohortReport = lazy(
  () =>
    import('@bbkAdminComponents/retained-revenue-cohort-report/retained-revenue-cohort-report')
);

const LiftDashboard = lazy(
  () => import('@bbkAdminComponents/lift-dashboard/lift-dashboard')
);

const ExperiencePreview = lazy(
  () => import('@bbkAdminComponents/designer/experience-preview')
);
const ExperienceFullPreview = lazy(
  () => import('@bbkAdminComponents/designer/experience-full-preview')
);
const TargetingPage = lazy(
  () => import('@bbkAdminComponents/experiences/targeting/targeting-page')
);
const NewEditRulePage = lazy(
  () =>
    import('@bbkAdminComponents/experiences/audiences/edit-rule/new-edit-rule-page')
);
const BrandingPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/branding')
);
const BrandListPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/brand-list')
);

const BrandDetailsPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/brand-details')
);

const NewBrandingPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/branding-drawer')
);

const GetStartedPage = lazy(
  () => import('@bbkAdminComponents/get-started/get-started-page')
);
const GetStartedPageCreateExperience = lazy(
  () => import('@bbkAdminComponents/get-started/create-experience-page')
);
const CustomDomain = lazy(
  () => import('@bbkAdminComponents/settings/custom-domain')
);

const CustomDomainNew = lazy(
  () => import('@bbkAdminComponents/settings/custom-domain-new')
);

const PredictiveCustomerPreview = lazy(
  () => import('@bbkAdminComponents/predictive/customers-preview')
);
const OptimizationsPage = lazy(
  () => import('@bbkAdminComponents/optimizer/OptimizationsPage')
);
const OptimizationEditorPage = lazy(
  () => import('@bbkAdminComponents/optimizer/OptimizationEditorPage')
);
const OptimizationDetailPage = lazy(
  () => import('@bbkAdminComponents/optimizer/OptimizationDetailPage')
);
const UnifiedRetentionPage = lazy(
  () => import('@bbkAdminComponents/unified-plays/UnifiedRetentionPage')
);
const SimpleCancelPagePriorityExample = lazy(
  () =>
    import('@bbkAdminComponents/unified-plays/examples/SimpleCancelPagePriorityExample')
);

const baseAuthPath = '/company/:companyKey/app/:appKey';

export const AuthorizedRouter: ReactFCC = () => {
  const history = useHistory();
  const createUrl = useUrl();

  const companyKey = useSelector(selectCompanyInternalName);
  const appKey = useSelector(selectAppId);
  const searchParams = useSearchParams<{ redirectUrl: string }>();
  const { hasBillingRole } = useRoles();

  usePendo();

  const enableAdminUsersPageOverride = useFeatureFlags(
    FeatureFlagsE.SHOW_ADMIN_USERS_PAGE_OVERRIDE
  );

  const enableVanityDomainNewFlow = useFeatureFlags(
    FeatureFlagsE.VANITY_DOMAIN_MIGRATED_TO_NEW_FLOW
  );

  const isBillingAdmin = hasBillingRole || enableAdminUsersPageOverride;

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);
  const audiencesManagementUrl = createUrl(AuthorizedPaths.audiencesList);

  const { isLoading: isFFLoading } = useQueryFeatureFlags();

  const pagesListUrl = useCreatePagesListUrl();

  if (isFFLoading) return <SuspenseLoader message=" " />;

  return (
    <Switch>
      <CompatRoute
        path="/company"
        exact
        render={() => <Redirect to={`/company/${companyKey}/`} />}
      />
      <CompatRoute
        path="/company/"
        exact
        render={() => <Redirect to={`/company/${companyKey}/`} />}
      />
      <CompatRoute
        path="/company/sign-in"
        exact
        render={() => (
          <Redirect
            to={searchParams.redirectUrl ?? `/company/${companyKey}/`}
          />
        )}
      />
      <CompatRoute
        path="/company/:companyKey/"
        exact
        render={() => <Redirect to={`/company/${companyKey}/app/`} />}
      />
      <CompatRoute
        path="/company/:companyKey/app/"
        exact
        render={() => <Redirect to={`/company/${companyKey}/app/${appKey}/`} />}
      />
      <CompatRoute
        path={`${baseAuthPath}/`}
        exact
        render={() => <Redirect to={createUrl(AuthorizedPaths.dashboard)} />}
      />
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.dashboard}`} exact>
        <MainContent>
          <Dashboard />
        </MainContent>
      </CompatRoute>
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.getStarted}`} exact>
        <MainContent>
          <GetStartedPage />
        </MainContent>
      </CompatRoute>
      {[
        `${baseAuthPath}${AuthorizedPaths.getStartedCreateExperienceStep1Branding}`,
        `${baseAuthPath}${AuthorizedPaths.getStartedCreateExperienceStep2Offers}`,
        `${baseAuthPath}${AuthorizedPaths.getStartedCreateExperienceStep3Targeting}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <GetStartedPageCreateExperience />
          </MainContent>
        </CompatRoute>
      ))}
      {[
        `${baseAuthPath}${AuthorizedPaths.experiencesFunnelPreview}`,
        `${baseAuthPath}${AuthorizedPaths.experiencesHostedPreview}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <ExperiencePreview />
          </MainContent>
        </CompatRoute>
      ))}
      {[
        `${baseAuthPath}${AuthorizedPaths.experiencesFunnelPreviewFull}`,
        `${baseAuthPath}${AuthorizedPaths.experiencesHostedPreviewFull}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <ExperienceFullPreview />
          </MainContent>
        </CompatRoute>
      ))}

      {/* Play */}
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.plays}`} exact>
        <MainContent>
          {agsLicense ? <PlayManager /> : <NotFoundPage />}
        </MainContent>
      </CompatRoute>

      {[
        `${baseAuthPath}${AuthorizedPaths.playEdit}`,
        `${baseAuthPath}${AuthorizedPaths.playNew}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            {agsLicense ? <PlayEditor editMode /> : <NotFoundPage />}
          </MainContent>
        </CompatRoute>
      ))}
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.playReports}`} exact>
        <MainContent>
          {agsLicense ? <PlayEditor editMode={false} /> : <NotFoundPage />}
        </MainContent>
      </CompatRoute>

      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.unifiedPlaysNew}`}
        exact
      >
        {agsLicense || growthV2License ? <CreateEditPlay /> : <NotFoundPage />}
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.unifiedPlaysEdit}`}
        exact
      >
        {agsLicense || growthV2License ? <CreateEditPlay /> : <NotFoundPage />}
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.unifiedPlaysReporting}`}
        exact
      >
        {agsLicense || growthV2License ? (
          <UnifiedReportingDashboard />
        ) : (
          <NotFoundPage />
        )}
      </CompatRoute>

      {/* Experiences */}
      <CompatRoute
        path={`${baseAuthPath}/experiences`}
        exact
        render={() => (
          <Redirect to={createUrl(AuthorizedPaths.experiencesBrandingPage)} />
        )}
      />
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesManager}`}
        exact
        render={() => <Redirect to={pagesListUrl} />}
      />
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesBrandingPage}`}
        exact
      >
        <MainContent>
          <BrandingPage />
        </MainContent>
      </CompatRoute>

      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.additionalBrandsPage}`}
        exact
      >
        <MainContent>
          <BrandListPage />
        </MainContent>
      </CompatRoute>
      {[
        `${baseAuthPath}${AuthorizedPaths.createAdditionalBrand}`,
        `${baseAuthPath}${AuthorizedPaths.viewAdditionalBrand}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <BrandDetailsPage />
          </MainContent>
        </CompatRoute>
      ))}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.editAdditionalBranding}`}
        exact
      >
        <MainContent>
          <NewBrandingPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesPages}`}
        exact
      >
        <MainContent>
          <PagesListPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingTableListPage}`}
        exact
      >
        <MainContent>
          <PricingPagesList />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingTableEditor}`}
        exact
      >
        <MainContent>
          <PricingPageEditor />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingTablePreview}`}
        exact
      >
        <MainContent>
          <PricingPagePreview />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingTablePreviewFull}`}
        exact
      >
        <MainContent>
          <PricingPageFullPreview />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.confirmationPages}`}
        exact
      >
        <MainContent>
          <ConfirmationPages />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.confirmationPagesCreate}`}
        exact
      >
        <MainContent>
          <ConfirmationPageDesigner />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.confirmationPagesEdit}`}
        exact
      >
        <MainContent>
          <ConfirmationPageDesigner />
        </MainContent>
      </CompatRoute>

      <CompatRoute // Targeting
        path={`${baseAuthPath}${AuthorizedPaths.experiencesTargeting}`}
        exact
      >
        <MainContent>
          <TargetingPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.audiencesList}`}
        exact
      >
        <MainContent>
          <AudiencesManagementPage />
        </MainContent>
      </CompatRoute>

      {[
        `${baseAuthPath}${AuthorizedPaths.audiencesNew}`,
        `${baseAuthPath}${AuthorizedPaths.audiencesEdit}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <NewEditRulePage
              open
              onClose={() => history.push(audiencesManagementUrl)}
              onSave={() => history.push(audiencesManagementUrl)}
            />
          </MainContent>
        </CompatRoute>
      ))}

      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesBrandManager}`}
        exact
      >
        <MainContent>
          <PagesListPage />
        </MainContent>
      </CompatRoute>
      {[
        `${baseAuthPath}${AuthorizedPaths.experiencesFunnelsPage}`,
        `${baseAuthPath}${AuthorizedPaths.experiencesHostedPage}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <Designer />
          </MainContent>
        </CompatRoute>
      ))}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesFunnelsPageConfirmation}`}
        exact
      >
        <MainContent>
          <Designer />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesFunnelsPageReasons}`}
        exact
        render={() => (
          <Redirect to={createUrl(AuthorizedPaths.experiencesPageReasons)} />
        )}
      />
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.experiencesPageReasons}`}
      >
        <MainContent>
          <ReasonsEditorPage />
        </MainContent>
      </CompatRoute>

      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.lossAversionNew}`}
        exact
      >
        <MainContent>
          <LossAversionNewPage />
        </MainContent>
      </CompatRoute>
      {[
        `${baseAuthPath}${AuthorizedPaths.lossAversionList}`,
        `${baseAuthPath}${AuthorizedPaths.lossAversionEdit}`,
        `${baseAuthPath}${AuthorizedPaths.lossAversionPlacement}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <LossAversionListPage />
          </MainContent>
        </CompatRoute>
      ))}

      {/* Offers */}
      {[
        `${baseAuthPath}${AuthorizedPaths.offers}`,
        `${baseAuthPath}${AuthorizedPaths.offersNew}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <OffersManagementPage />
          </MainContent>
        </CompatRoute>
      ))}

      {[
        `${baseAuthPath}${AuthorizedPaths.offersEdit}`,
        `${baseAuthPath}${AuthorizedPaths.offersPlacement}`,
        `${baseAuthPath}${AuthorizedPaths.offersBilling}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditDesign}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditActivities}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditItems}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditSettings}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditCtaSettings}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditCtaState}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditAITopLevel}`,
        `${baseAuthPath}${AuthorizedPaths.offersEditBranding}`,
        `${baseAuthPath}${AuthorizedPaths.offerPosition}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          <MainContent>
            <OffersEditOfferPage />
          </MainContent>
        </CompatRoute>
      ))}

      {/* Trends */}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.trendsGraphs}`}
        exact
      >
        <MainContent>
          <TrendsReportGeneral />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}/trends/:trendsPage`}
        exact
        render={() => (
          <Redirect to={createUrl(AuthorizedPaths.trendsCancels)} />
        )}
      />

      {/* Insights */}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.insightsCancelAI}`}
        exact
      >
        <MainContent>
          <AttributionInsights isAI />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.insightsCancel}`}
        exact
      >
        <MainContent>
          <AttributionInsights />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.insightsExperiencePerformanceReport}`}
        exact
      >
        <MainContent>
          <ExperiencePerformanceReportV2 />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.insightsOfferPerformance}`}
        exact
      >
        <MainContent>
          <OfferPerformance />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.insightsRetainedRevenueCohort}`}
        exact
      >
        <MainContent>
          <RetainedRevenueCohortReport />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.insightsLiftDashboard}`}
        exact
      >
        <MainContent>
          <LiftDashboard />
        </MainContent>
      </CompatRoute>

      {/* Customers */}
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.customers}`} exact>
        <MainContent>
          <CustomersPage />
        </MainContent>
      </CompatRoute>
      {/* Customer Details */}
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.customersDetail}`}>
        <MainContent>
          <CustomerDetailsPage />
        </MainContent>
      </CompatRoute>

      {/* Settings */}
      <CompatRoute path={`${baseAuthPath}${AuthorizedPaths.settings}`} exact>
        <MainContent>
          <Settings />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsSubscriptions}`}
        exact
      >
        <MainContent>
          <SubscriptionsPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsAlerts}`}
        exact
      >
        <MainContent>
          <AlertEditPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsWebhooks}`}
        exact
      >
        <MainContent>
          <WebhookEditPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsIntegrations}`}
        exact
      >
        <MainContent>
          <Integrations />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsSetup}`}
        exact
      >
        <MainContent>
          <Setup />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsDevelopers}`}
        exact
      >
        <MainContent>
          <Developers />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsPermissions}`}
        exact
      >
        <MainContent>
          <Permissions />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsRestrictedDomainsManage}`}
        exact
      >
        <MainContent>
          <Users />
        </MainContent>
      </CompatRoute>
      {[
        `${baseAuthPath}${AuthorizedPaths.settingsCustomDomain}`,
        `${baseAuthPath}${AuthorizedPaths.settingsCustomDomainNew}`,
        `${baseAuthPath}${AuthorizedPaths.settingsCustomDomainStatusPage}`,
      ].map((p) => (
        <CompatRoute path={p} exact key={p}>
          {enableVanityDomainNewFlow ? <CustomDomainNew /> : <CustomDomain />}
        </CompatRoute>
      ))}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsFieldMappings}`}
        exact
      >
        <MainContent>
          <DataManagement />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsInstall}`}
        exact
      >
        <MainContent>
          <Install />
        </MainContent>
      </CompatRoute>

      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsUsers}`}
        exact
      >
        <MainContent>
          {isBillingAdmin ? <Users /> : <NotFoundPage />}
        </MainContent>
      </CompatRoute>

      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsPlans}`}
        exact
      >
        <MainContent>
          <Plans />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsUsersInvite}`}
        exact
      >
        <MainContent>
          <InviteUsers />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.settingsActivityLog}`}
        exact
      >
        <MainContent>
          <ActivityLogPage />
        </MainContent>
      </CompatRoute>
      {/* Predictive */}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.viewPredictiveCustomerPreview}`}
        exact
      >
        <MainContent>
          {isPredictiveEnabled ? (
            <PredictiveCustomerPreview />
          ) : (
            <NotFoundPage />
          )}
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingOptimizations}`}
        exact
      >
        <MainContent>
          <OptimizationsPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingOptimizationDetail}`}
        exact
      >
        <MainContent>
          <OptimizationDetailPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingOptimizationEditReviewSimulate}`}
        exact
      >
        <MainContent>
          <OptimizationEditorPage />
        </MainContent>
      </CompatRoute>
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.pricingOptimizationEdit}`}
      >
        <MainContent>
          <OptimizationEditorPage />
        </MainContent>
      </CompatRoute>
      {[
        `${baseAuthPath}${AuthorizedPaths.unifiedPlaysRetention}`,
        `${baseAuthPath}${AuthorizedPaths.unifiedPlaysAcquisition}`,
        `${baseAuthPath}${AuthorizedPaths.unifiedPlaysExpansion}`,
      ].map((p) => (
        <CompatRoute key={p} path={p}>
          <UnifiedRetentionPage />
        </CompatRoute>
      ))}
      <CompatRoute
        path={`${baseAuthPath}${AuthorizedPaths.unifiedPlaysCancelPagePriorityExample}`}
        exact
      >
        <MainContent>
          {agsLicense ? <SimpleCancelPagePriorityExample /> : <NotFoundPage />}
        </MainContent>
      </CompatRoute>
      <CompatRoute>
        <NotFoundPage />
      </CompatRoute>
    </Switch>
  );
};
